'use client';

import React, { useState, useEffect } from 'react';
import { RefreshCw, Clock } from 'lucide-react';

const REFRESH_INTERVAL = 300; // 5 minutes in seconds

export default function DiscoveryHeader() {
  const [secondsLeft, setSecondsLeft] = useState(REFRESH_INTERVAL);
  const [refreshing, setRefreshing] = useState(false);
  const [lastSynced, setLastSynced] = useState('');

  useEffect(() => {
    // Backend: get last sync timestamp from API
    const now = new Date();
    const mins = now?.getMinutes()?.toString()?.padStart(2, '0');
    const hours = now?.getHours()?.toString()?.padStart(2, '0');
    setLastSynced(`Today at ${hours}:${mins}`);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          handleRefresh();
          return REFRESH_INTERVAL;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    // Backend: trigger segment data refresh for current search params
    setTimeout(() => {
      setRefreshing(false);
      const now = new Date();
      const mins = now?.getMinutes()?.toString()?.padStart(2, '0');
      const hours = now?.getHours()?.toString()?.padStart(2, '0');
      setLastSynced(`Today at ${hours}:${mins}`);
    }, 1500);
  };

  const mins = Math.floor(secondsLeft / 60);
  const secs = secondsLeft % 60;
  const progress = ((REFRESH_INTERVAL - secondsLeft) / REFRESH_INTERVAL) * 100;

  return (
    <div className="px-6 py-4 border-b border-[hsl(var(--border))] bg-white flex items-center justify-between gap-4 shrink-0">
      <div>
        <h1 className="text-lg font-bold text-[hsl(var(--foreground))]">Segment Discovery</h1>
        <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
          Segments ranked by your win probability — highest first
        </p>
      </div>
      <div className="flex items-center gap-4">
        {/* Auto-refresh countdown */}
        <div className="flex items-center gap-2 text-xs text-[hsl(var(--muted-foreground))]">
          <div className="relative w-5 h-5">
            <svg className="w-5 h-5 -rotate-90" viewBox="0 0 20 20">
              <circle cx="10" cy="10" r="8" fill="none" stroke="hsl(var(--border))" strokeWidth="2" />
              <circle
                cx="10" cy="10" r="8"
                fill="none"
                stroke="hsl(var(--primary))"
                strokeWidth="2"
                strokeDasharray={`${2 * Math.PI * 8}`}
                strokeDashoffset={`${2 * Math.PI * 8 * (1 - progress / 100)}`}
                className="transition-all duration-1000"
              />
            </svg>
          </div>
          <span className="font-mono tabular-nums">
            Refresh in {mins}:{secs?.toString()?.padStart(2, '0')}
          </span>
        </div>

        {lastSynced && (
          <div className="flex items-center gap-1.5 text-xs text-[hsl(var(--muted-foreground))]">
            <Clock size={11} />
            <span>Synced {lastSynced}</span>
          </div>
        )}

        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-[hsl(var(--muted))] hover:bg-[hsl(var(--border))] text-[hsl(var(--foreground))] transition-colors disabled:opacity-60"
        >
          <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} />
          <span>{refreshing ? 'Refreshing...' : 'Refresh now'}</span>
        </button>
      </div>
    </div>
  );
}